import {
  AlertTriangle,
  ArrowLeft,
  Heart,
  Info,
  Pill,
  RefreshCw,
  Shield,
  Sparkles,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NudgeBanner } from "../../components/ui/NudgeBanner";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  AggregatedHealthAdvice,
  AiTripAnalysis,
  ForumHealthSummary,
  getAiTripAnalysis,
  getForumHealthSummary,
  getHealthAdvice,
  getOperatorHealthData,
  OperatorHealthData,
} from "../../services/api/travelHealth";
import { getTravel, Travel } from "../../services/api/travels";
import { useAuthStore } from "../../stores/authStore";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import { categoryLabel, toDateOnly } from "../../utils/travelHealthLabels";

const categoryMeta: Record<
  string,
  { icon: React.ElementType; color: string; bg: string }
> = {
  vaccination: { icon: Shield, color: "text-primary-600", bg: "bg-primary-50" },
  medication: { icon: Pill, color: "text-primary-600", bg: "bg-primary-50" },
  precaution: {
    icon: AlertTriangle,
    color: "text-orange-600",
    bg: "bg-orange-50",
  },
  advisory: { icon: Info, color: "text-yellow-600", bg: "bg-yellow-50" },
  warning: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
};

const sevOrder = ["critical", "high", "medium", "low", "info"];

export const RecommendationsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const { user } = useAuthStore();
  const [travel, setTravel] = useState<Travel | null>(null);
  const [advice, setAdvice] = useState<AggregatedHealthAdvice | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<AiTripAnalysis | null>(null);
  const [forumSummary, setForumSummary] = useState<ForumHealthSummary | null>(
    null,
  );
  const [operatorData, setOperatorData] = useState<OperatorHealthData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);
  const [loadingForum, setLoadingForum] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadTravel(id);
  }, [id]);

  const loadTravel = async (travelId: string) => {
    try {
      setLoading(true);
      const data = await getTravel(travelId);
      setTravel(data);
      await loadAdvice(data);
      // Load operator health data if operator specified (AC 6)
      if ((data as any).tripOperator) {
        getOperatorHealthData(
          data.tripType ?? "flight",
          (data as any).tripOperator,
          data.destination,
        )
          .then(setOperatorData)
          .catch(() => null);
      }
    } catch (err: any) {
      console.error("Failed to load travel:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAdvice = useCallback(
    async (travelData: Travel) => {
      if (!travelData.country || !travelData.departureDate) return;
      try {
        setLoadingAdvice(true);
        setError(null);
        const data = await getHealthAdvice(
          travelData.country,
          toDateOnly(travelData.departureDate),
          toDateOnly(travelData.returnDate ?? travelData.departureDate),
          travelData.tripType ?? "flight",
          user?.country ?? undefined,
        );
        setAdvice(data);
      } catch (err: any) {
        setError(
          t.healthDataLoadError ?? "Could not load health recommendations.",
        );
      } finally {
        setLoadingAdvice(false);
      }
    },
    [user?.country, t],
  );

  const loadAiAnalysis = async () => {
    if (!id) return;
    try {
      setLoadingAi(true);
      const data = await getAiTripAnalysis(id);
      setAiAnalysis(data);
    } catch (err: any) {
      setError(t.aiAnalysisError ?? "AI analysis not available.");
    } finally {
      setLoadingAi(false);
    }
  };

  // AC 9: Load forum health summary on demand
  const loadForumSummary = async () => {
    // Both destination and country are optional on Travel and the endpoint
    // requires them; without this the request went out with `undefined`.
    if (!travel?.destination || !travel.country) return;
    try {
      setLoadingForum(true);
      const data = await getForumHealthSummary(
        travel.destination,
        travel.country,
        (travel as any).activities ?? [],
        travel.tripType ?? "flight",
      );
      setForumSummary(data);
    } catch {
      // non-critical
    } finally {
      setLoadingForum(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const recs = advice?.recommendations ?? [];
  const grouped = recs.reduce(
    (acc, r) => {
      const bucket = acc[r.category] ?? [];
      bucket.push(r);
      acc[r.category] = bucket;
      return acc;
    },
    {} as Record<string, typeof recs>,
  );

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/traveler/travels")}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <PageBanner
          icon={Heart}
          title={t.personalRecommendations ?? "Personal Recommendations"}
          description={travel ? `${travel.destination}, ${travel.country}` : ""}
        />
      </div>

      {/* Source banner */}
      {advice && (
        <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 text-sm text-primary-800">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 shrink-0" />
            <span>
              {t.dataFrom ?? "Data from"}:{" "}
              {advice.sources?.map((s) => s.name).join(", ")} ·{" "}
              {fmt.date(advice.lastUpdated)}
            </span>
          </div>
          <button
            onClick={() => travel && loadAdvice(travel)}
            disabled={loadingAdvice}
            className="flex items-center gap-1 hover:text-primary-600"
          >
            <RefreshCw
              className={`h-4 w-4 ${loadingAdvice ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      )}

      {error && (
        <NudgeBanner
          variant="warning"
          icon={<AlertTriangle className="h-4 w-4" />}
          className="text-sm"
        >
          {error}
        </NudgeBanner>
      )}

      {/* Intro summary */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-50 rounded-xl border border-primary-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {t.personalizedHealthRecommendations ??
            "Personalized Health Recommendations"}
        </h2>
        <p className="text-gray-700 text-sm">
          {t.recommendationsIntro ??
            "Based on your destination, trip duration, and health profile – evidence-based, multi-source recommendations."}
          {travel && (
            <>
              {" "}
              <strong>{travel.country}</strong>.
            </>
          )}
        </p>
      </div>

      {/* AI Analysis (PREMIUM) */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            {t.aiTripAnalysis ?? "AI Trip Health Analysis"}
            <span className="px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700 font-medium">
              PREMIUM
            </span>
          </h2>
          {!aiAnalysis && (
            <button
              onClick={loadAiAnalysis}
              disabled={loadingAi}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-60"
            >
              {loadingAi ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {t.analyzing ?? "Analyzing…"}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {t.runAiAnalysis ?? "Run AI Analysis"}
                </>
              )}
            </button>
          )}
        </div>

        {!aiAnalysis && !loadingAi && (
          <p className="text-sm text-gray-500">
            {t.aiAnalysisHint ??
              "Climate impact and personal health profile analysis powered by AI."}
          </p>
        )}

        {aiAnalysis && (
          <div className="space-y-4">
            <div
              className={`p-4 rounded-lg border ${aiAnalysis.overallRiskLevel === "high" || aiAnalysis.overallRiskLevel === "critical" ? "bg-red-50 border-red-200" : aiAnalysis.overallRiskLevel === "medium" ? "bg-yellow-50 border-yellow-200" : "bg-green-50 border-green-200"}`}
            >
              <p className="font-medium text-gray-900 mb-1">
                {t.climateRiskSummary ?? "Climate Risk Summary"}
              </p>
              <p className="text-sm text-gray-700">
                {aiAnalysis.climateRiskSummary}
              </p>
            </div>
            {aiAnalysis.healthProfileRisks?.length > 0 && (
              <div>
                <p className="font-medium text-gray-900 mb-2">
                  {t.healthProfileRisks ?? "Your Health Profile Risks"}
                </p>
                <ul className="space-y-1">
                  {aiAnalysis.healthProfileRisks.map((risk, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" />
                      {risk}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {aiAnalysis.urgentActions?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-800 mb-2">
                  {t.urgentActions ?? "Urgent Actions"}
                </p>
                <ul className="space-y-1">
                  {aiAnalysis.urgentActions.map((a, i) => (
                    <li
                      key={i}
                      className="text-sm text-red-700 flex items-start gap-2"
                    >
                      <span className="shrink-0 font-bold">!</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-gray-400 italic">
              {aiAnalysis.disclaimer}
            </p>
          </div>
        )}
      </div>

      {/* Evidence-based recommendations by category */}
      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([category, items]) => {
          const meta = categoryMeta[category] ?? {
            icon: Heart,
            color: "text-primary-600",
            bg: "bg-primary-50",
          };
          const Icon = meta.icon;
          const sorted = [...items].sort(
            (a, b) =>
              sevOrder.indexOf(a.severity) - sevOrder.indexOf(b.severity),
          );
          return (
            <div key={category} className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${meta.bg}`}>
                  <Icon className={`h-5 w-5 ${meta.color}`} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {categoryLabel(t, category)}
                  </h3>
                  <p className="text-xs text-gray-400">
                    {sorted
                      .map((r) => r.source?.name)
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .join(", ")}
                  </p>
                </div>
                <span className="ml-auto text-sm text-gray-400">
                  {items.length}
                </span>
              </div>
              <ul className="space-y-3">
                {sorted.map((r, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div
                      className={`w-2 h-2 rounded-full mt-2 shrink-0 ${meta.color.replace("text-", "bg-")}`}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {r.title}
                      </p>
                      <p className="text-sm text-gray-600">{r.description}</p>
                      {r.details && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {r.details}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

      {/* AC 6: operator-specific health data. This was fetched and thrown
          away - setOperatorData was called but nothing ever rendered it. */}
      {operatorData && (
        <div
          data-testid="operator-health-data"
          className="bg-white rounded-xl shadow p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-600" />
            {operatorData.operatorName
              ? `Operator guidance: ${operatorData.operatorName}`
              : "Operator guidance"}
          </h2>

          {operatorData.operatorAdvisory && (
            <p className="text-gray-700 mb-4">
              {operatorData.operatorAdvisory}
            </p>
          )}

          {operatorData.cruiseHealthRequirements.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium text-gray-900 mb-2">
                Health requirements
              </h3>
              <ul className="space-y-2">
                {operatorData.cruiseHealthRequirements.map((req, i) => (
                  <li
                    key={`${req.category}-${i}`}
                    className="p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">
                        {req.title}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded bg-primary-100 text-primary-700">
                        {req.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {req.description}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Source: {req.source}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {operatorData.generalAdvisory && (
            <p className="text-sm text-gray-500">
              {operatorData.generalAdvisory}
            </p>
          )}
        </div>
      )}

      {/* AC 9: traveller forum summary, loaded on demand. loadForumSummary
          existed but was never called from anywhere. */}
      <div
        data-testid="forum-health-summary"
        className="bg-white rounded-xl shadow p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Info className="w-5 h-5 text-teal-600" />
            What other travellers report
          </h2>
          {!forumSummary && (
            <button
              type="button"
              onClick={loadForumSummary}
              disabled={loadingForum}
              className="px-4 py-2 text-sm rounded-lg border border-teal-600 text-teal-700 hover:bg-teal-50 disabled:opacity-50"
            >
              {loadingForum ? "Loading..." : "Load traveller reports"}
            </button>
          )}
        </div>

        {forumSummary ? (
          <div className="space-y-4 text-sm">
            {forumSummary.commonHealthIssues.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  Commonly reported issues
                </h3>
                <ul className="list-disc list-inside text-gray-600">
                  {forumSummary.commonHealthIssues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {forumSummary.foodWaterSafety && (
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  Food and water
                </h3>
                <p className="text-gray-600">{forumSummary.foodWaterSafety}</p>
              </div>
            )}

            {forumSummary.localHealthcareSummary && (
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  Local healthcare
                </h3>
                <p className="text-gray-600">
                  {forumSummary.localHealthcareSummary}
                </p>
              </div>
            )}

            {forumSummary.travelerTips.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  Traveller tips
                </h3>
                <ul className="list-disc list-inside text-gray-600">
                  {forumSummary.travelerTips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}

            {forumSummary.disclaimer && (
              <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                {forumSummary.disclaimer}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Summarized from public traveller forums. Not medical advice.
          </p>
        )}
      </div>

      {recs.length === 0 && !loadingAdvice && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          <p>
            {t.noRecommendationsAvailable ??
              "No specific recommendations found for this destination."}
          </p>
        </div>
      )}
    </div>
  );
};
